import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Image,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { observer } from '@legendapp/state/react';
import { Ionicons } from '@expo/vector-icons';
import { getChatSessions } from '../utils/chatHelpers';
import { characters$ } from '../utils/Supa';
import { Tables } from '../utils/database.types';

interface SessionWithCharacter {
  session: Tables<'chat_sessions'>;
  character: Tables<'characters'> | undefined;
}

const MessagesScreen = observer(({ onSelectCharacter }: { onSelectCharacter: (character: Tables<'characters'>) => void }) => {
  const insets = useSafeAreaInsets();
  const [sessions, setSessions] = useState<SessionWithCharacter[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getChatSessions()
      .then((data) => {
        const chars = characters$.get() || {};
        const mapped = data.map((s) => ({
          session: s,
          character: Object.values(chars).find((c) => c.id === s.character_id),
        }));
        if (mounted) {
          setSessions(mapped);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const renderItem = ({ item }: { item: SessionWithCharacter }) => {
    const char = item.character;
    if (!char) return null;
    return (
      <TouchableOpacity
        style={styles.row}
        activeOpacity={0.8}
        onPress={() => onSelectCharacter(char)}
      >
        {char.avatar_url ? (
          <Image source={{ uri: char.avatar_url }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatarPlaceholder, { backgroundColor: char.accent_color }]}>
            <Text style={styles.avatarText}>{char.name[0]}</Text>
          </View>
        )}
        <View style={styles.textWrap}>
          <Text style={styles.name}>{char.name}</Text>
          <Text style={styles.date} numberOfLines={1}>
            {new Date(item.session.updated_at).toLocaleDateString()}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={20} color="#6E728F" />
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <View style={{ height: insets.top }} />
      <Text style={styles.header}>Messages</Text>

      {loading ? (
        <View style={styles.center}>
          <Text style={styles.emptyText}>Loading conversations...</Text>
        </View>
      ) : sessions.length === 0 ? (
        <View style={styles.center}>
          <Ionicons name="chatbubble-ellipses-outline" size={48} color="#4A4F6B" />
          <Text style={styles.emptyTitle}>No messages yet</Text>
          <Text style={styles.emptyText}>Start chatting with a character from the Home tab.</Text>
        </View>
      ) : (
        <FlatList
          data={sessions}
          renderItem={renderItem}
          keyExtractor={(item) => item.session.id}
          contentContainerStyle={styles.list}
          showsVerticalScrollIndicator={false}
        />
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#080B1F',
    paddingHorizontal: 16,
  },
  header: {
    color: '#F5F7FF',
    fontSize: 32,
    fontWeight: '700',
    marginTop: 12,
    marginBottom: 20,
  },
  list: {
    paddingBottom: 20,
    gap: 10,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#191D33',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 12,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
  },
  avatarPlaceholder: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: '#F6F7FF',
    fontSize: 20,
    fontWeight: '600',
  },
  textWrap: {
    flex: 1,
  },
  name: {
    color: '#FFFFFF',
    fontSize: 17,
    fontWeight: '600',
  },
  date: {
    color: '#9DA4C9',
    fontSize: 13,
    marginTop: 2,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 24,
  },
  emptyTitle: {
    color: '#F7F8FF',
    fontSize: 20,
    fontWeight: '700',
  },
  emptyText: {
    color: '#9DA4C9',
    fontSize: 14,
    textAlign: 'center',
  },
});

export default MessagesScreen;
